import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons/faCheckCircle';
import React, { useMemo, useCallback } from 'react';
import classnames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

import style from './style.module.less';
import { FormattedCurrency } from '../../../../../components/formatted-currency';
import { BillingPlanIds, isBillingPlanIdUsageBilledHighVolume } from '../../../../../enums/billing-plan-ids';
import {
  EU_PLAN_PROFILE_CODES,
  UK_PLAN_PROFILE_CODES,
  PLAN_PROFILES_WITH_PER_LABEL_FEE,
  PlanProfiles,
} from '../../../../../enums/plan-profiles';
import {
  useIsNonWalletAccessVisible,
  getNonWalletAccessDisplayPrice,
  getNonWalletAccessDisplayPriceNote,
} from '../../../../../hooks/non-wallet-access-hooks';
import Global from '../../../../../store/global-selectors';
import * as constants from '../../constants';
import { useManageSubscriptionDrawerContext } from '../../context/manage-subscription-drawer-context';
import { getPlanShipmentLimitMessage, useGetNumberOfIncludedUserSeatsForPlan } from '../../hooks/use-plan-details';
import { messages } from '../../messages';
import { PerLabelFee } from '../per-label-fee';
import { localizedPlanNameMessageDescriptor } from '../plan-name-helper';
import { PremiumFeatures } from '../premium-features';
import { PriceValue } from '../price-value';
import { SupportValue } from '../support-value';

const PlanShipmentLimit = ({ plan }) => {
  return getPlanShipmentLimitMessage(plan);
};

const PlanUsersIncluded = ({ billingPlanId }) => {
  return useGetNumberOfIncludedUserSeatsForPlan(billingPlanId);
};

const HIGH_VOLUME_LEGACY_PLAN_NAME = 'High Volume (Legacy)';

export const PlanGrid = ({ onSelectPlan = noop }) => {
  const { selectedPlanId, setSelectedPlanId, setByoaEnabled, pendingPlanIdInGrid, recommendedPlanIds } =
    useManageSubscriptionDrawerContext();
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const shouldShowByoaSection = useIsNonWalletAccessVisible();
  const { allPlans = [], planProfileCode = '', currentBillingPlan = {}, planToNonWalletAccessPlanMap = {} } = subscriptionData ?? {};
  const currentBillingPlanId = currentBillingPlan?.billingPlanId;

  // Preserve legacy behavior: non-trial users see current plan as recommended
  const effectiveRecommendedPlanIds = useMemo(() => {
    const isNonTrialUser = currentBillingPlanId && parseInt(currentBillingPlanId, 10) !== BillingPlanIds.Trial;
    if (isNonTrialUser) {
      return [currentBillingPlanId];
    }
    return recommendedPlanIds;
  }, [currentBillingPlanId, recommendedPlanIds]);

  const onSelectPlanClick = useCallback(
    (e) => {
      e.stopPropagation();
      const currentlySelectedPlanId = e.currentTarget.getAttribute('data-billing-plan-id');

      setSelectedPlanId(currentlySelectedPlanId);
      onSelectPlan(currentlySelectedPlanId);

      const isHighVolume = isBillingPlanIdUsageBilledHighVolume(currentlySelectedPlanId);

      if (shouldShowByoaSection && isHighVolume) {
        setByoaEnabled(true);
      }
    },
    [onSelectPlan, setByoaEnabled, setSelectedPlanId, shouldShowByoaSection],
  );

  const intl = useIntl();

  const isOnByoaPlan = !!Object.values(planToNonWalletAccessPlanMap).find((bp) => bp.billingPlanId === currentBillingPlan?.billingPlanId);

  const modifiedAllPlans = useMemo(() => {
    const plans = allPlans.map((plan) => {
      if (plan.name === currentBillingPlan?.name) {
        return {
          ...(isOnByoaPlan ? plan : currentBillingPlan),
          billingPlanId: currentBillingPlan?.billingPlanId,
          nonByoaBillingPlanId: plan?.billingPlanId,
        };
      }
      return plan;
    });

    // insert plan side by side if necessary
    const isSellerPlanInGrid = plans.some((plan) => plan.billingPlanId === currentBillingPlan?.billingPlanId);
    if (!isSellerPlanInGrid && currentBillingPlan?.name === HIGH_VOLUME_LEGACY_PLAN_NAME) {
      plans.splice(plans.length - 1, 0, {
        ...currentBillingPlan,
        billingPlanId: currentBillingPlan?.billingPlanId,
      });
    }
    return plans;
  }, [allPlans, currentBillingPlan, isOnByoaPlan]);

  const shouldShowPremiumFeaturesSection = modifiedAllPlans.some((plan) => plan.subscriptionLineItems.length > 0);
  const shouldShowCarrierLimits = modifiedAllPlans.some((plan) => constants.FREE_POSTAL === plan.code);

  const shouldShowOrdersLimitLabel = planProfileCode === PlanProfiles.uk_billing_orders.planProfileCode;
  const shippingLimitLabel = shouldShowOrdersLimitLabel ? (
    <FormattedMessage {...messages.ordersPerMonth} />
  ) : (
    <FormattedMessage {...messages.shipmentsPerMonth} />
  );

  const brandingValue = (hasBranding) => {
    if (hasBranding && EU_PLAN_PROFILE_CODES.includes(planProfileCode)) {
      return <FormattedMessage {...messages.customizable} />;
    }

    if (UK_PLAN_PROFILE_CODES.includes(planProfileCode)) {
      return hasBranding ? <FormattedMessage {...messages.customizable} /> : <FormattedMessage {...messages.standard} />;
    }

    return hasBranding ? <FormattedMessage {...messages.customized} /> : <FormattedMessage {...messages.branded} />;
  };

  const carrierValue = (code) => {
    return code === constants.FREE_POSTAL ? <FormattedMessage {...messages.uspsCarrier} /> : <FormattedMessage {...messages.unlimited} />;
  };

  const shouldShowPerLabelFee = PLAN_PROFILES_WITH_PER_LABEL_FEE.includes(planProfileCode);

  const modifiedRecommendedPlanIds = useMemo(
    () => modifiedAllPlans.filter((plan) => effectiveRecommendedPlanIds.includes(plan.billingPlanId)).map((plan) => plan.billingPlanId),
    [modifiedAllPlans, effectiveRecommendedPlanIds],
  );

  const currentPlanId =
    parseInt(currentBillingPlan?.billingPlanId, 10) !== BillingPlanIds.Trial ? currentBillingPlan?.billingPlanId : undefined;

  const getExtraMessage = useCallback(
    (billingPlanId) => {
      if (billingPlanId === currentPlanId) {
        return <FormattedMessage {...messages.currentPlan} />;
      }
      if (modifiedRecommendedPlanIds.includes(billingPlanId)) {
        return <FormattedMessage {...messages.recommendedPlan} />;
      }
      if (billingPlanId === pendingPlanIdInGrid) {
        return <FormattedMessage {...messages.pendingPlan} />;
      }
    },
    [currentPlanId, modifiedRecommendedPlanIds, pendingPlanIdInGrid],
  );

  return (
    <div className={style['plan-grid-container']}>
      <div className={classnames(style['plan-container'], style['header-column'])}>
        <div className={classnames(style['plan-item'], style['recommended-cell'], style.empty)} />
        <div className={style['select-plan']}>
          <FormattedMessage {...messages.selectPlan} />
        </div>
        <div className={style['header-item']}>
          <FormattedMessage {...messages.pricePerMonth} />
        </div>
        {shouldShowPerLabelFee ? (
          <div className={style['header-item']}>
            <FormattedMessage {...messages.perLabelFee} />
          </div>
        ) : null}
        <div className={style['header-item']}>{shippingLimitLabel}</div>
        <div className={style['header-item']}>
          <FormattedMessage {...messages.planUsersLimit} />
        </div>
        <div className={style['header-item']}>
          <FormattedMessage {...messages.planPackingSlipsAndLabelsBranding} />
        </div>
        <div className={style['header-item']}>
          <FormattedMessage {...messages.planSupportLevel} />
        </div>
        {shouldShowByoaSection ? (
          <div className={style['header-item']}>
            <FormattedMessage {...messages.yourCarriersAddOnPrice} />
          </div>
        ) : null}
        {shouldShowPremiumFeaturesSection ? (
          <div className={style['header-item']}>
            <FormattedMessage {...messages.planPremiumFeatures} />
          </div>
        ) : null}
      </div>
      {modifiedAllPlans.map((plan) => {
        const byoaSisterPlan = planToNonWalletAccessPlanMap[plan.billingPlanId] ?? planToNonWalletAccessPlanMap[plan.nonByoaBillingPlanId];
        const isSelected = selectedPlanId === plan.billingPlanId || selectedPlanId === plan.nonByoaBillingPlanId;
        const { messageDescriptor, values } = localizedPlanNameMessageDescriptor(plan.name);
        const byoaAddOnPrice = getNonWalletAccessDisplayPrice(plan, byoaSisterPlan);
        const fractionDigits = Number.isInteger(byoaAddOnPrice) ? 0 : 2;
        const byoaPriceNote = getNonWalletAccessDisplayPriceNote(plan);
        const extraMessage = getExtraMessage(plan.billingPlanId);
        const planIncludedFeatureIds = plan.subscriptionLineItems.reduce(
          (acc, lineItem) => (lineItem.isIncludedInPlan ? [...acc, ...lineItem.featureIds] : acc),
          [],
        );
        return (
          <div
            key={plan.billingPlanId}
            aria-label={intl.formatMessage(messageDescriptor)}
            className={classnames(style['plan-container'])}
            data-billing-plan-id={plan.billingPlanId}
            onClick={onSelectPlanClick}
          >
            <div className={classnames(style['plan-item'], style['recommended-cell'], { [style.selected]: isSelected && !!extraMessage })}>
              {extraMessage}
            </div>
            <div
              className={classnames(style.plan, {
                [style.selected]: isSelected,
              })}
            >
              <div className={style['plan-name']}>
                {isSelected ? <FontAwesomeIcon className={style['selected-icon']} icon={faCheckCircle} /> : null}
                <FormattedMessage {...messageDescriptor} values={values} />
              </div>
              <div className={style['plan-features']}>
                <div className={style['plan-price']}>
                  <PriceValue plan={plan} />
                </div>
                {shouldShowPerLabelFee ? (
                  <div className={style['plan-item']}>
                    <b>
                      <PerLabelFee plan={plan} />
                    </b>
                  </div>
                ) : null}
                <div className={style['plan-item']}>
                  <PlanShipmentLimit plan={plan} />
                </div>
                {shouldShowCarrierLimits ? <div className={style['plan-item']}>{carrierValue(plan.code)}</div> : null}
                <div className={style['plan-item']}>
                  <PlanUsersIncluded billingPlanId={plan.billingPlanId} />
                </div>
                <div className={style['plan-item']}>{brandingValue(plan.hasBranding)}</div>
                <div className={style['plan-item']}>
                  <SupportValue plan={plan} />
                </div>
                {shouldShowByoaSection ? (
                  <div className={style['plan-item']}>
                    <FormattedCurrency currencyCode={plan?.pricePerPeriod?.code} fractionDigits={fractionDigits} value={byoaAddOnPrice} />
                    {byoaPriceNote}
                  </div>
                ) : null}
                {shouldShowPremiumFeaturesSection ? (
                  <div className={style['plan-item']}>
                    <PremiumFeatures includedFeatureIds={planIncludedFeatureIds} />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

PlanGrid.propTypes = {
  onSelectPlan: PropTypes.func,
};
